import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import ModalHeader from 'react-bootstrap/ModalHeader';
import ModalTitle from 'react-bootstrap/ModalTitle';

import BackArrow from '../../assets/svgs/BackArrow.js';
import './FgiModal.scss';

/**
 * FgiModal Component
 *
 * A customizable modal component built using react-bootstrap.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The content to be displayed inside the modal body.
 * @param {boolean} props.show - Controls the visibility of the modal.
 * @param {function} props.onHide - Callback function triggered when the modal is requested to be hidden (e.g., clicking the close button or outside the modal).
 * @param {string} [props.title] - The title displayed in the modal header.
 * @param {string|React.ReactNode} [props.description] - An optional description displayed below the header.
 * @param {boolean} [props.showCloseButton=true] - Whether to display the close button in the header.
 * @param {string} [props.className] - Additional CSS classes to apply to the modal container.
 * @param {boolean} [props.showBackButton=false] - Whether to display a back button in the header.
 * @param {function} [props.backClick] - Callback function triggered when the back button is clicked.
 * @param {string} [props.color='yellow'] - The color theme of the modal (affects the color bar).
 * @param {object} [props.restProps] - Any other props accepted by the react-bootstrap Modal component.
 * @returns {JSX.Element} The rendered modal component.
 *
 * @example
 * ```jsx
 *   <FgiModal
 *     show={showModal}
 *     onHide={handleCloseModal}
 *     title="Example Modal Title"
 *     description="This is an optional description for the modal."
 *     color="blue" // Example: using a different color
 *     showBackButton={true} // Example: showing the back button
 *     backClick={handleBackClick} // Example: handling back button click
 *     // You can pass other react-bootstrap Modal props like 'size', 'centered', etc.
 *     // size="lg"
 *     // centered
 *   >
 *     <p>This is the main content inside the modal body.</p>
 *     <p>You can put any React nodes here.</p>
 *     <Button variant="secondary" onClick={handleCloseModal}>
 *       Close Inside
 *     </Button>
 *   </FgiModal>
 * ```
 */
const FgiModal = ({
  children,
  show,
  onHide,
  title,
  description,
  showCloseButton,
  className,
  showBackButton,
  backClick,
  color = 'yellow',
  ...restProps
}) => (
  <Modal
    onHide={onHide}
    show={show}
    className={`fgi-modal ${className}`}
    {...restProps}
  >
    <ModalHeader>
      {showBackButton && (
        <div className="modal-back-arrow back" onClick={backClick}>
          <BackArrow />
        </div>
      )}
      <ModalTitle>
        <span className={`colorBar colorBar__${color}`} />
        <h4 className="title-modal">{title}</h4>
      </ModalTitle>
      {showCloseButton && (
        <div onClick={onHide} className="close">
          <span>×</span>
        </div>
      )}
    </ModalHeader>
    {description && (
      <div className="fgi-modal__description description">{description}</div>
    )}
    <ModalBody>{children}</ModalBody>
    <div className="fgi-modal__bottom-part" />
  </Modal>
);

FgiModal.defaultProps = {
  showCloseButton: true,
  showBackButton: false,
};

FgiModal.propTypes = {
  /**It is an optional string property that represents the color of the modal.  */
  color: PropTypes.string,
  /**required node property that represents the content of the modal.  */
  children: PropTypes.node.isRequired,
  /**required boolean property that determines whether the modal is visible or not.  */
  show: PropTypes.bool.isRequired,
  /** It is a required function property that handles the event when the modal is closed or hidden. */
  onHide: PropTypes.func.isRequired,
  /**It is an optional string property that allows you to add custom CSS classes to the modal */
  className: PropTypes.string,
  /** It is an optional string property that represents the title of the modal. */
  title: PropTypes.string,
  /**t is an optional property that can be either a string or a node */
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** It is an optional boolean property that determines whether to show a close button in the modal*/
  showCloseButton: PropTypes.bool,
  /** It is an optional boolean property that determines whether to show a back button in the modal*/
  showBackButton: PropTypes.bool,
};

export default FgiModal;
